import { useEffect } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { supabase } from '@/integrations/supabase/client';

/**
 * Injects tenant-specific favicon and social meta tags into <head>.
 * Runs whenever a public tenant is resolved (subdomain or custom domain).
 * Non-destructive: only updates the specific tags it manages.
 */
function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setFavicon(url: string) {
  // Remove existing icon links to avoid the default overriding the tenant one
  document.head
    .querySelectorAll<HTMLLinkElement>('link[rel~="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
    .forEach((el) => el.parentNode?.removeChild(el));
  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = url;
  // Let browser sniff the type (works for png/svg/ico/webp)
  document.head.appendChild(link);
}

export function TenantHead() {
  const { userId, isSubdomain, isCustomDomain } = useTenant();

  useEffect(() => {
    if (!userId || (!isSubdomain && !isCustomDomain)) return;

    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('site_settings')
        .select('favicon_url, social_image_url, site_title, site_description')
        .eq('user_id', userId)
        .limit(1)
        .maybeSingle();

      if (cancelled || !data) return;

      const s = data as {
        favicon_url: string | null;
        social_image_url: string | null;
        site_title: string | null;
        site_description: string | null;
      };

      if (s.favicon_url) setFavicon(s.favicon_url);

      if (s.social_image_url) {
        upsertMeta('property', 'og:image', s.social_image_url);
        upsertMeta('name', 'twitter:image', s.social_image_url);
        upsertMeta('name', 'twitter:card', 'summary_large_image');
      }

      if (s.site_title) {
        upsertMeta('property', 'og:title', s.site_title);
        upsertMeta('name', 'twitter:title', s.site_title);
      }

      if (s.site_description) {
        upsertMeta('name', 'description', s.site_description);
        upsertMeta('property', 'og:description', s.site_description);
        upsertMeta('name', 'twitter:description', s.site_description);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, isSubdomain, isCustomDomain]);

  return null;
}
